(() => {
  'use strict';

  const NAMESPACE = '__tm24CollectionSearchV11';
  const existing = window[NAMESPACE];
  if (existing) {
    existing.mount();
    return;
  }

  const D = window.TM24_SEARCH_DICTIONARY || {};
  const synonymNode = document.getElementById('TM24SearchSynonyms');
  const LOCAL_SYNONYMS = (() => {
    try {
      return JSON.parse(synonymNode?.textContent || '{}');
    } catch (_) {
      return {};
    }
  })();

  const CACHE = window.__tm24CollectionSearchCacheV11 ||= new Map();
  const ALTERNATIVE_CACHE = window.__tm24CollectionAlternativeCacheV1 ||= new Map();
  const INSTANCES = new Map();
  const GLOBAL_EVENTS = new AbortController();
  try {
    localStorage.removeItem('tm24SearchLearn8');
    localStorage.removeItem('tm24SearchLearn5Words');
  } catch (_) {}

  const normalize = (value) => String(value || '')
    .toLocaleLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ß/g, 'ss')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
  const compact = (value) => normalize(value).replace(/\s+/g, '');
  const words = (value) => normalize(value).split(/\s+/).filter(Boolean);
  const codeCompact = (value) => compact(value);
  const hasDigit = (value) => /\d/.test(String(value || ''));
  const controlledQuery = (value, language) => {
    const original = String(value || '').trim();
    if (!original) return '';
    let corrected = original.replace(/\b([a-z]{1,5})[\s\-./]+(\d{2,}[a-z0-9]*)\b/gi, '$1$2');
    const corrections = LOCAL_SYNONYMS.corrections?.[language] || {};
    corrected = corrections[normalize(corrected)] || corrected;
    const manufacturerAliases = LOCAL_SYNONYMS.manufacturerAliases || {};
    corrected = corrected.split(/(\s+)/).map((part) => (
      /^\s+$/.test(part) ? part : (corrections[normalize(part)] || manufacturerAliases[normalize(part)] || part)
    )).join('');
    return corrected.trim();
  };
  const templateText = (value, query, count, collection = '') => String(value || '')
    .replaceAll('__query__', query)
    .replaceAll('__count__', String(count))
    .replaceAll('__collection__', collection);
  const headerCartCount = () => [...document.querySelectorAll('cart-count')].reduce((maximum, node) => {
    const current = Number.parseInt(String(node.textContent || '').replace(/[^0-9]/g, ''), 10);
    return Number.isFinite(current) ? Math.max(maximum, current) : maximum;
  }, 0);
  const ensureHeaderCartCount = (minimum) => {
    document.querySelectorAll('cart-count').forEach((node) => {
      const current = Number.parseInt(String(node.textContent || '').replace(/[^0-9]/g, ''), 10);
      const next = Math.max(Number.isFinite(current) ? current : 0, Number(minimum || 0));
      node.textContent = String(next);
      node.toggleAttribute('hidden', next <= 0);
      const label = node.getAttribute('aria-label') || '';
      if (/\d+/.test(label)) node.setAttribute('aria-label', label.replace(/\d+/, String(next)));
    });
  };

  const queryTokens = (value) => {
    const input = words(value);
    const output = [];
    for (let index = 0; index < input.length; index += 1) {
      if (/^[a-z]{1,4}$/.test(input[index]) && input[index + 1] && /\d/.test(input[index + 1])) {
        output.push(input[index] + input[index + 1]);
        index += 1;
      } else {
        output.push(input[index]);
      }
    }
    return output;
  };

  const capitalise = (value) => String(value || '')
    .trim()
    .replace(/(^|[\s/-])([\p{L}])/gu, (_, prefix, letter) => prefix + letter.toLocaleUpperCase(document.documentElement.lang || 'de'))
    .replace(/\b(8k2|fbs|ose|sks|gfa|mfz|bft|faac|bbc|bea|ral|led|mwb2|mwbc|wbp2000|cs\s?\d+|ts\s?\d+|hse\s?\d+(?:\s?bs)?|hs\s?\d+(?:\s?bs)?|hsm\s?\d+|hss\s?\d+|hsp\s?\d+|fct\s?\d+|fit\s?\d+|skx\s?\d+)\b/gi, (match) => match.toUpperCase());

  function editDistance(left, right, maximum = 1) {
    const a = compact(left);
    const b = compact(right);
    if (a === b) return 0;
    if (Math.abs(a.length - b.length) > maximum) return maximum + 1;

    let previous = [...Array(b.length + 1).keys()];
    let previousPrevious = null;
    for (let rowIndex = 1; rowIndex <= a.length; rowIndex += 1) {
      const row = [rowIndex];
      let rowMinimum = rowIndex;
      for (let columnIndex = 1; columnIndex <= b.length; columnIndex += 1) {
        let value = Math.min(
          previous[columnIndex] + 1,
          row[columnIndex - 1] + 1,
          previous[columnIndex - 1] + (a[rowIndex - 1] === b[columnIndex - 1] ? 0 : 1),
        );
        if (
          previousPrevious
          && rowIndex > 1
          && columnIndex > 1
          && a[rowIndex - 1] === b[columnIndex - 2]
          && a[rowIndex - 2] === b[columnIndex - 1]
        ) {
          value = Math.min(value, previousPrevious[columnIndex - 2] + 1);
        }
        row[columnIndex] = value;
        rowMinimum = Math.min(rowMinimum, value);
      }
      if (rowMinimum > maximum) return maximum + 1;
      previousPrevious = previous;
      previous = row;
    }
    return previous[b.length];
  }

  function controlledGroups(language) {
    const output = [];
    const map = new Map();
    const add = (primary, aliases = [], type = 'term') => {
      const values = [primary, ...aliases].map((value) => String(value || '').trim()).filter(Boolean);
      const key = `${type}|${compact(values[0])}`;
      if (!values.length) return;
      const existingGroup = map.get(key);
      if (existingGroup) {
        values.forEach((value) => {
          const normal = normalize(value);
          if (!normal || existingGroup.normal.includes(normal)) return;
          existingGroup.aliases.push(value);
          existingGroup.normal.push(normal);
          existingGroup.compact.push(compact(value));
        });
        return;
      }
      const seen = new Set();
      const unique = values.filter((value) => {
        const normal = normalize(value);
        if (!normal || seen.has(normal)) return false;
        seen.add(normal);
        return true;
      });
      const group = {
        primary: unique[0],
        aliases: unique,
        type,
        normal: unique.map(normalize),
        compact: unique.map(compact),
        count: 0,
      };
      map.set(key, group);
      output.push(group);
    };

    (LOCAL_SYNONYMS[language] || LOCAL_SYNONYMS.de || []).forEach((group) => add(group[0], group.slice(1), 'synonym'));
    if (language === 'de') {
      const localManufacturerAliases = new Map();
      Object.entries(LOCAL_SYNONYMS.manufacturerAliases || {}).forEach(([alias, primary]) => {
        const label = String(primary || '').trim();
        if (!label) return;
        const key = compact(label);
        const aliases = localManufacturerAliases.get(key) || { label, values: [] };
        aliases.values.push(alias);
        localManufacturerAliases.set(key, aliases);
      });
      localManufacturerAliases.forEach((manufacturer) => {
        add(manufacturer.label, manufacturer.values, 'manufacturer');
      });
      (D.manufacturers || []).forEach((item) => {
        add(item.label, item.aliases || [], 'manufacturer');
        (item.modelAliases || []).forEach((model) => add(model, [model], 'model'));
      });
      (D.families || []).forEach((item) => {
        add(item.label, [...(item.aliases || []), item.search], 'family');
        (item.modelAliases || []).forEach((model) => add(model, [model], 'model'));
      });
      (D.synonyms || []).forEach((item) => add(item.replace, item.aliases || [], 'synonym'));
      (D.problemIntents || []).forEach((item) => {
        add(item.primarySearch || String(item.family || '').split('/')[0].trim() || item.search || item.label, item.aliases || [], 'intent');
      });
    }
    return output;
  }

  function appendHighlightedText(target, label, query) {
    const needle = normalize(query);
    const source = String(label || '');
    if (!needle) {
      target.textContent = source;
      return;
    }
    const sourceNormal = normalize(source);
    const index = sourceNormal.indexOf(needle);
    if (index < 0 || sourceNormal.length !== source.toLocaleLowerCase().length) {
      target.textContent = source;
      return;
    }
    target.append(document.createTextNode(source.slice(0, index)));
    const mark = document.createElement('mark');
    mark.textContent = source.slice(index, index + needle.length);
    target.append(mark, document.createTextNode(source.slice(index + needle.length)));
  }

  class CollectionSearch {
    constructor(root) {
      this.root = root;
      this.parent = root.parentElement;
      this.form = root.querySelector('[data-search-form]');
      this.input = root.querySelector('[data-search-input]');
      this.clear = root.querySelector('[data-search-clear]');
      this.status = root.querySelector('[data-search-status]');
      this.cartStatus = root.querySelector('[data-cart-status]');
      this.results = root.querySelector('[data-search-results]');
      this.summary = root.querySelector('[data-search-summary]');
      this.list = root.querySelector('[data-search-list]');
      this.more = root.querySelector('[data-search-more]');
      this.suggestionsBox = root.querySelector('[data-search-suggestions]');
      this.suggestionsList = root.querySelector('[data-suggestion-terms]');
      this.suggestionsWrap = root.querySelector('[data-suggestion-terms-wrap]');
      this.suggestionsEmpty = root.querySelector('[data-suggestion-empty]');
      this.filterNote = root.querySelector('[data-filter-note-output]');
      this.otherCollections = root.querySelector('[data-other-collections]');
      this.otherCollectionsHeading = root.querySelector('[data-other-collections-heading]');
      this.otherCollectionList = root.querySelector('[data-other-collection-list]');

      this.path = root.dataset.collectionPath || location.pathname.match(/^(.*\/collections\/[^/]+)/)?.[1] || location.pathname;
      this.collectionHandle = root.dataset.collectionHandle || this.path.split('/').filter(Boolean).at(-1) || '';
      this.language = (document.documentElement.lang || 'de').split('-')[0];
      this.currency = root.dataset.currency || window.Shopify?.currency?.active || 'EUR';
      this.pageSize = Math.max(1, Number(root.dataset.pageSize || 12));
      this.products = [];
      this.matches = [];
      this.groups = [];
      this.renderedHandles = new Set();
      this.hiddenSiblings = new Map();
      this.serial = 0;
      this.activeSuggestion = -1;
      this.timer = 0;
      this.loadPromise = null;
      this.loadController = null;
      this.alternativeController = null;
      this.alternativeTimer = 0;
      this.moreBusy = false;
      this.destroyed = false;
      this.events = new AbortController();

      this.refreshSourceState();
      this.bind();

      const initialQuery = new URL(location.href).searchParams.get('tm24_q') || '';
      this.syncFilterForms(initialQuery);
      if (initialQuery) {
        this.input.value = initialQuery;
        this.clear.hidden = false;
        queueMicrotask(() => this.search({ force: true, closeSuggestions: true, historyMode: 'none' }));
      }
    }

    refreshSourceState() {
      const params = new URLSearchParams(location.search);
      ['page', 'section_id', 'view', 'tm24_q'].forEach((key) => params.delete(key));
      this.sourceParams = params.toString();
      this.cacheKey = `${this.path}?${this.sourceParams}|${this.language}|${this.currency}|v10`;
    }

    bind() {
      const signal = this.events.signal;
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        const items = this.suggestionItems();
        if (this.activeSuggestion >= 0 && items[this.activeSuggestion]) {
          items[this.activeSuggestion].click();
          return;
        }
        this.search({ force: true, closeSuggestions: true, historyMode: 'push' });
      }, { signal });

      this.form.addEventListener('reset', () => {
        setTimeout(() => {
          this.input.value = '';
          this.clear.hidden = true;
          this.reset({ historyMode: 'push' });
          this.input.focus();
        }, 0);
      }, { signal });

      this.input.addEventListener('input', () => {
        this.clear.hidden = !this.input.value;
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.search({ historyMode: 'replace' }), 170);
      }, { signal });
      this.input.addEventListener('focus', () => {
        if (normalize(this.input.value).length >= 2) this.search({ suggestionsOnly: true, historyMode: 'none' });
      }, { signal });
      this.input.addEventListener('keydown', (event) => this.handleKeydown(event), { signal });
      this.more.addEventListener('click', () => this.appendMore(), { signal });
      this.suggestionsList.addEventListener('click', (event) => {
        const button = event.target.closest('[data-suggestion-item]');
        if (!button || !this.suggestionsList.contains(button)) return;
        this.input.value = button.dataset.suggestionValue || button.textContent.trim();
        this.clear.hidden = false;
        this.closeSuggestions();
        this.search({ force: true, closeSuggestions: true, historyMode: 'push' });
      }, { signal });
      this.list.addEventListener('click', (event) => {
        const button = event.target.closest('[data-cart-add]');
        if (!button || !this.list.contains(button)) return;
        const product = this.matches.find((item) => item.handle === button.dataset.productHandle);
        if (product) this.addToCart(product, button);
      }, { signal });
      document.addEventListener('pointerdown', (event) => {
        if (!this.root.contains(event.target)) this.closeSuggestions();
      }, { signal });
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;
      clearTimeout(this.timer);
      clearTimeout(this.alternativeTimer);
      this.serial += 1;
      this.loadController?.abort();
      this.alternativeController?.abort();
      this.events.abort();
      this.restoreDefaultGrid();
      INSTANCES.delete(this.root);
    }

    suggestionItems() {
      return [...this.suggestionsBox.querySelectorAll('[data-suggestion-item]')];
    }

    handleKeydown(event) {
      const items = this.suggestionItems();
      if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && items.length) {
        event.preventDefault();
        this.activeSuggestion = (this.activeSuggestion + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
        items.forEach((item, index) => {
          const active = index === this.activeSuggestion;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        this.input.setAttribute('aria-activedescendant', items[this.activeSuggestion].id);
        items[this.activeSuggestion].scrollIntoView({ block: 'nearest' });
      } else if (event.key === 'Enter' && this.activeSuggestion >= 0 && items[this.activeSuggestion]) {
        event.preventDefault();
        items[this.activeSuggestion].click();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.closeSuggestions();
      }
    }

    openSuggestions() {
      const hasContent = this.suggestionItems().length || (this.suggestionsEmpty && !this.suggestionsEmpty.hidden);
      if (!hasContent) return;
      this.suggestionsBox.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    closeSuggestions() {
      this.suggestionsBox.hidden = true;
      this.activeSuggestion = -1;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
    }

    updateHistory(query, mode = 'replace') {
      this.syncFilterForms(query);
      if (mode === 'none') return;
      const url = new URL(location.href);
      const value = String(query || '').trim();
      const previousValue = url.searchParams.get('tm24_q') || '';
      if (value) url.searchParams.set('tm24_q', value);
      else url.searchParams.delete('tm24_q');
      if (url.href === location.href) return;
      const state = { ...(history.state || {}), tm24CollectionSearch: true };
      const method = mode === 'push' || (mode === 'replace' && value && !previousValue) ? 'pushState' : 'replaceState';
      history[method](state, '', url);
    }

    syncFilterForms(query) {
      const value = String(query || '').trim();
      document.querySelectorAll('form').forEach((form) => {
        if (!form.querySelector('[name^="filter."], [name="sort_by"]')) return;
        let input = form.querySelector('input[data-tm24-query-state]');
        if (!value) {
          input?.remove();
          return;
        }
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'tm24_q';
          input.dataset.tm24QueryState = '';
          form.append(input);
        }
        input.value = value;
      });
    }

    applyUrlState() {
      const query = new URL(location.href).searchParams.get('tm24_q') || '';
      if (this.input.value === query && ((query && !this.results.hidden) || (!query && this.results.hidden))) return;
      this.input.value = query;
      this.clear.hidden = !query;
      if (query) this.search({ force: true, closeSuggestions: true, historyMode: 'none' });
      else this.reset({ historyMode: 'none' });
    }

    hideDefaultGrid() {
      [...this.parent.children].forEach((element) => {
        if (element === this.root) return;
        if (!this.hiddenSiblings.has(element)) this.hiddenSiblings.set(element, element.hidden);
        element.hidden = true;
      });
    }

    restoreDefaultGrid() {
      this.hiddenSiblings.forEach((wasHidden, element) => {
        if (element.isConnected) element.hidden = wasHidden;
      });
      this.hiddenSiblings.clear();
    }

    hasActiveFilters() {
      return [...new URLSearchParams(this.sourceParams).keys()].some((key) => key.startsWith('filter.'));
    }

    clearAlternativeLookup({ clearStatus = false } = {}) {
      clearTimeout(this.alternativeTimer);
      this.alternativeTimer = 0;
      this.alternativeController?.abort();
      this.alternativeController = null;
      if (this.otherCollectionList) this.otherCollectionList.replaceChildren();
      if (this.otherCollections) {
        this.otherCollections.hidden = true;
        this.otherCollections.setAttribute('aria-busy', 'false');
      }
      if (this.filterNote) {
        this.filterNote.hidden = true;
        this.filterNote.textContent = '';
      }
      if (clearStatus) this.status.textContent = '';
    }

    safeAlternativeQuery(value) {
      return String(value || '')
        .normalize('NFKC')
        .replace(/[^\p{L}\p{N}\s-]+/gu, ' ')
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 8)
        .join(' ')
        .slice(0, 120);
    }

    hintProductMatches(item, query) {
      const matchText = [
        item.match_text,
        item.title,
        item.type,
        item.sku,
        item.manufacturer,
        item.tortyp,
        item.productType,
        item.variant_text,
        item.tags,
      ].join(' ');
      const matchNormal = normalize(matchText);
      const matchCompact = compact(matchText);
      const matchWords = new Set(words(matchText));
      const groups = controlledGroups(this.language);
      let tokens = queryTokens(query).filter((token) => !this.stopwords.has(normalize(token)));
      if (!tokens.length) tokens = queryTokens(query);
      if (!tokens.length) return false;

      return tokens.every((token) => {
        const tokenNormal = normalize(token);
        const tokenCompact = compact(token);
        if (!tokenNormal) return true;
        if (hasDigit(token)) {
          return matchCompact.includes(codeCompact(token));
        }
        if (matchWords.has(tokenNormal) || matchNormal.includes(tokenNormal) || matchCompact.includes(tokenCompact)) {
          return true;
        }
        return groups.some((group) => this.groupMatchesQuery(group, token) && group.aliases.some((alias) => {
          const aliasNormal = normalize(alias);
          const aliasCompact = compact(alias);
          return matchWords.has(aliasNormal) || matchNormal.includes(aliasNormal) || matchCompact.includes(aliasCompact);
        }));
      });
    }

    collectionLinks(item) {
      if (Array.isArray(item.collection_links)) return item.collection_links.slice(0, 12);
      const handles = String(item.collection_handles || '').split('|');
      const titles = String(item.collection_titles || '').split('|');
      return handles.map((handle, index) => ({ handle, title: titles[index] || handle })).slice(0, 12);
    }

    resolveAlternativeCollections(items, query) {
      const queryNormal = normalize(query);
      const queryCompact = compact(query);
      let queryParts = queryTokens(query).filter((token) => !this.stopwords.has(normalize(token)));
      if (!queryParts.length) queryParts = queryTokens(query);
      const queryPartNormals = queryParts.map(normalize).filter(Boolean);
      const utilityCollection = /^(?:all|frontpage|tm-ersatzteilfinder|avada-best-sellers)$/;
      const utilityTitle = /\b(?:bestseller|top produkte|ersatzteilfinder)\b/i;
      const map = new Map();

      items.slice(0, 50).forEach((item, itemIndex) => {
        if (!item || typeof item !== 'object' || !this.hintProductMatches(item, query)) return;
        const productKey = String(item.handle || item.title || item.sku || itemIndex);
        const productType = String(item.productType || item.type || '').trim();
        this.collectionLinks(item).forEach((collection) => {
          const handle = String(collection?.handle || '').trim();
          const title = String(collection?.title || '').trim();
          if (
            !handle
            || !title
            || !/^[a-z0-9][a-z0-9-]{0,254}$/.test(handle)
            || handle === this.collectionHandle
            || utilityCollection.test(handle)
            || utilityTitle.test(title)
          ) return;
          const entry = map.get(handle) || {
            handle,
            title,
            products: new Set(),
            productTypes: new Set(),
            bestIndex: itemIndex,
          };
          entry.products.add(productKey);
          if (productType) entry.productTypes.add(productType);
          entry.bestIndex = Math.min(entry.bestIndex, itemIndex);
          map.set(handle, entry);
        });
      });

      return [...map.values()].map((entry) => {
        const collectionText = `${entry.title} ${entry.handle}`;
        const collectionNormal = normalize(collectionText);
        const collectionCompact = compact(collectionText);
        const queryHits = queryPartNormals.filter((token) => (
          collectionNormal.includes(token) || collectionCompact.includes(compact(token))
        )).length;
        let typeHits = 0;
        entry.productTypes.forEach((type) => {
          const meaningful = words(type).filter((token) => token.length >= 4);
          if (meaningful.some((token) => collectionNormal.includes(token))) typeHits += 1;
        });
        const startsWithQuery = queryCompact.length >= 3 && collectionCompact.startsWith(queryCompact);
        const allQueryParts = queryPartNormals.length > 0 && queryHits === queryPartNormals.length;
        const semanticScore = (startsWithQuery ? 10000 : 0)
          + (allQueryParts ? 5000 : 0)
          + (queryHits * 1600)
          + (typeHits * 700);
        if (!semanticScore && entry.products.size < 2) return null;
        return {
          handle: entry.handle,
          title: entry.title,
          score: semanticScore + (entry.products.size * 120) - entry.bestIndex,
        };
      }).filter(Boolean).sort((left, right) => (
        right.score - left.score
        || left.title.localeCompare(right.title, this.language, { numeric: true })
        || left.handle.localeCompare(right.handle)
      )).slice(0, 3);
    }

    async fetchAlternativeCollections(query) {
      const safeQuery = this.safeAlternativeQuery(query);
      const minimumLength = hasDigit(safeQuery) ? 2 : 3;
      if (normalize(safeQuery).length < minimumLength) return [];
      const previewTheme = new URL(location.href).searchParams.get('preview_theme_id') || '';
      const cacheKey = `${this.language}|${this.collectionHandle}|${compact(safeQuery)}|${previewTheme}`;
      if (ALTERNATIVE_CACHE.has(cacheKey)) return ALTERNATIVE_CACHE.get(cacheKey);

      const controller = new AbortController();
      this.alternativeController = controller;
      const routeRoot = String(window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
      const url = new URL(`${routeRoot}search`, location.origin);
      const currentParams = new URL(location.href).searchParams;
      ['preview_theme_id', 'pb', '_fd', '_ab', 'theme_id'].forEach((key) => {
        const value = currentParams.get(key);
        if (value) url.searchParams.set(key, value);
      });
      url.searchParams.set('section_id', 'tm24-finder-search-source');
      url.searchParams.set('q', safeQuery);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      url.searchParams.set('page', '1');

      try {
        const response = await fetch(url, {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const wrapper = documentFragment.querySelector('[data-tm24-search-wrap]');
        const source = documentFragment.querySelector('[data-tm24-search-json]');
        if (!wrapper || !source) throw new Error('Ungültige Suchquelle');
        let items;
        try {
          items = JSON.parse(source.textContent || '[]');
        } catch (_) {
          throw new Error('Ungültige Suchdaten');
        }
        if (!Array.isArray(items)) throw new Error('Ungültige Suchliste');
        const alternatives = this.resolveAlternativeCollections(items, query);
        ALTERNATIVE_CACHE.set(cacheKey, alternatives);
        if (ALTERNATIVE_CACHE.size > 20) ALTERNATIVE_CACHE.delete(ALTERNATIVE_CACHE.keys().next().value);
        return alternatives;
      } finally {
        if (this.alternativeController === controller) this.alternativeController = null;
      }
    }

    alternativeUrl(handle, query) {
      const routeRoot = String(window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
      const url = new URL(`${routeRoot}collections/${encodeURIComponent(handle)}`, location.origin);
      const currentParams = new URL(location.href).searchParams;
      ['preview_theme_id', 'pb', '_fd', '_ab', 'theme_id'].forEach((key) => {
        const value = currentParams.get(key);
        if (value) url.searchParams.set(key, value);
      });
      url.searchParams.set('tm24_q', String(query || '').trim());
      return url.href;
    }

    renderAlternativeCollections(collections, query) {
      if (!this.otherCollections || !this.otherCollectionList || !this.otherCollectionsHeading) return;
      this.otherCollectionList.replaceChildren();
      collections.forEach((collection) => {
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.className = 'tm24-collection-search__alternative-link';
        link.href = this.alternativeUrl(collection.handle, query);
        link.setAttribute('aria-label', templateText(this.root.dataset.otherAria, query, collections.length, collection.title));
        const title = document.createElement('span');
        title.className = 'tm24-collection-search__alternative-title';
        title.textContent = collection.title;
        const action = document.createElement('span');
        action.className = 'tm24-collection-search__alternative-action';
        action.textContent = this.root.dataset.otherLink || '';
        link.append(title, action);
        item.append(link);
        this.otherCollectionList.append(item);
      });
      this.otherCollectionsHeading.textContent = this.root.dataset.otherHeading || '';
      this.otherCollections.hidden = !collections.length;
      this.otherCollections.setAttribute('aria-busy', 'false');
      if (collections.length) {
        this.closeSuggestions();
        const text = collections.length === 1 ? this.root.dataset.otherFoundOne : this.root.dataset.otherFoundOther;
        this.status.textContent = templateText(text, query, collections.length);
      } else {
        this.status.textContent = '';
      }
    }

    scheduleAlternativeCollections(query, effectiveQuery, requestId, immediate = false) {
      if (this.hasActiveFilters()) {
        if (this.filterNote) {
          this.filterNote.textContent = this.root.dataset.filterNote || '';
          this.filterNote.hidden = false;
        }
        return;
      }
      const safeQuery = this.safeAlternativeQuery(effectiveQuery);
      const minimumLength = hasDigit(safeQuery) ? 2 : 3;
      if (normalize(safeQuery).length < minimumLength) return;
      this.status.textContent = this.root.dataset.otherLoading || '';
      if (this.otherCollections) this.otherCollections.setAttribute('aria-busy', 'true');
      const run = async () => {
        try {
          const collections = await this.fetchAlternativeCollections(effectiveQuery);
          if (requestId !== this.serial || this.destroyed || this.matches.length) return;
          this.renderAlternativeCollections(collections, query);
        } catch (error) {
          if (requestId !== this.serial || this.destroyed || error.name === 'AbortError') return;
          this.status.textContent = '';
          if (this.otherCollections) {
            this.otherCollections.hidden = true;
            this.otherCollections.setAttribute('aria-busy', 'false');
          }
        }
      };
      this.alternativeTimer = setTimeout(run, immediate ? 0 : 320);
    }

    async fetchPage(page, signal) {
      const url = new URL(this.path, location.origin);
      for (const [key, value] of new URLSearchParams(this.sourceParams)) url.searchParams.append(key, value);
      url.searchParams.set('page', String(page));
      url.searchParams.set('section_id', 'tm24-finder-collection-source');
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
        signal,
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const wrapper = documentFragment.querySelector('[data-tm24-source-wrap]');
      const source = documentFragment.querySelector('[data-tm24-source-json]');
      if (!wrapper || !source) throw new Error('Ungültige Collection-Quelle');
      const pages = Number(wrapper.dataset.tm24Pages || 1);
      if (!Number.isFinite(pages) || pages < 1) throw new Error('Ungültige Pagination');
      let items;
      try {
        items = JSON.parse(source.textContent || '[]');
      } catch (_) {
        throw new Error('Ungültige Produktdaten');
      }
      if (!Array.isArray(items)) throw new Error('Ungültige Produktliste');
      return { pages, items: items.map((item) => this.fromRich(item)) };
    }

    fromRich(item) {
      const variants = Array.isArray(item.variant_index) ? item.variant_index : [];
      return this.prepareProduct({
        handle: item.handle,
        title: item.title,
        url: item.url,
        image: item.image,
        priceCents: Number(item.price_cents || 0),
        available: item.available !== false,
        manufacturer: item.manufacturer,
        productType: item.productType || item.type,
        doorType: item.tortyp,
        group: item.ersatzteilgruppe,
        series: item.baureihe,
        keywords: item.keywords,
        references: item.refs,
        compatible: item.passend_fuer,
        designation: item.bezeichnung,
        altTexts: item.alt_texts,
        tags: item.tags,
        variantText: item.variant_text,
        variants,
        sku: item.sku,
        variantId: item.variant_id,
        hasOnlyDefaultVariant: item.has_only_default_variant === true,
      });
    }

    prepareProduct(product) {
      const searchableFields = [
        product.title,
        product.handle,
        product.manufacturer,
        product.productType,
        product.doorType,
        product.group,
        product.series,
        product.keywords,
        product.references,
        product.compatible,
        product.designation,
        product.altTexts,
        product.tags,
        product.variantText,
      ];
      const codeValues = [
        product.sku,
        product.series,
        ...String(product.references || '').split(/[\s,;|]+/),
        ...(product.variants || []).flatMap((variant) => [variant.sku, variant.barcode, variant.title]),
      ].map(codeCompact).filter((value) => value && /\d/.test(value));

      product.searchNormal = normalize(searchableFields.join(' '));
      product.searchCompact = compact(searchableFields.join(' '));
      product.searchWords = new Set(words(searchableFields.join(' ')));
      product.titleNormal = normalize(product.title);
      product.titleCompact = compact(product.title);
      product.manufacturerNormal = normalize(product.manufacturer);
      product.manufacturerCompact = compact(product.manufacturer);
      product.typeNormal = normalize(product.productType);
      product.typeCompact = compact(product.productType);
      product.seriesNormal = normalize(product.series);
      product.seriesCompact = compact(product.series);
      product.tagNormal = normalize(product.tags);
      product.codeValues = new Set(codeValues);
      return product;
    }

    groupMatchesQuery(group, query) {
      const queryNormal = normalize(query);
      const queryCompact = compact(query);
      for (let index = 0; index < group.aliases.length; index += 1) {
        const aliasNormal = group.normal[index];
        const aliasCompact = group.compact[index];
        if (
          aliasNormal === queryNormal
          || aliasCompact === queryCompact
          || aliasNormal.startsWith(queryNormal)
          || aliasCompact.startsWith(queryCompact)
          || queryNormal.startsWith(aliasNormal)
        ) return true;
        if (hasDigit(query) && hasDigit(group.aliases[index]) && codeCompact(group.aliases[index]) === codeCompact(query)) return true;
        if (!hasDigit(query) && queryNormal.length >= 5 && aliasNormal.length >= 5) {
          const maximum = Math.max(queryNormal.length, aliasNormal.length) >= 10 ? 2 : 1;
          if (editDistance(queryNormal, aliasNormal, maximum) <= maximum) return true;
        }
      }
      return false;
    }

    productMatchesGroup(product, group) {
      if (group.type === 'manufacturer') {
        return group.compact.some((alias) => alias === product.manufacturerCompact);
      }
      if (group.type === 'model') {
        return group.compact.some((alias) => (
          alias === product.seriesCompact
          || product.codeValues.has(alias)
          || product.titleCompact.includes(alias)
        ));
      }
      return group.aliases.some((alias) => {
        const aliasNormal = normalize(alias);
        const aliasCompact = compact(alias);
        return product.searchNormal.includes(aliasNormal) || product.searchCompact.includes(aliasCompact);
      });
    }

    buildGroups() {
      const groups = controlledGroups(this.language);
      const actual = new Map();
      const addActual = (label, type) => {
        const value = String(label || '').trim();
        const key = `${type}|${compact(value)}`;
        if (!value || key.endsWith('|')) return;
        const entry = actual.get(key) || {
          primary: value,
          aliases: [value],
          type,
          normal: [normalize(value)],
          compact: [compact(value)],
          count: 0,
        };
        entry.count += 1;
        actual.set(key, entry);
      };
      this.products.forEach((product) => {
        addActual(product.manufacturer, 'manufacturer');
        addActual(product.productType, 'family');
        addActual(product.group, 'family');
        addActual(product.series, 'series');
      });
      groups.forEach((group) => {
        group.count = this.products.reduce((count, product) => count + (this.productMatchesGroup(product, group) ? 1 : 0), 0);
      });
      actual.forEach((group) => {
        const actualCompact = compact(group.primary);
        const coveredByControlledGroup = groups.some((controlled) => (
          controlled.type === group.type && controlled.compact.includes(actualCompact)
        ));
        if (!coveredByControlledGroup) groups.push(group);
      });
      this.groups = groups.filter((group) => group.count > 0);
    }

    async load() {
      const previousKey = this.cacheKey;
      this.refreshSourceState();
      if (previousKey !== this.cacheKey) {
        this.loadController?.abort();
        this.loadPromise = null;
        this.products = [];
        this.groups = [];
      }
      if (this.products.length) return;
      if (CACHE.has(this.cacheKey)) {
        this.products = CACHE.get(this.cacheKey);
        this.buildGroups();
        return;
      }
      if (this.loadPromise) return this.loadPromise;

      const controller = new AbortController();
      this.loadController = controller;
      const promise = (async () => {
        const all = [];
        const first = await this.fetchPage(1, controller.signal);
        all.push(...first.items);
        for (let start = 2; start <= first.pages; start += 4) {
          const batch = [];
          for (let page = start; page < start + 4 && page <= first.pages; page += 1) batch.push(this.fetchPage(page, controller.signal));
          const results = await Promise.all(batch);
          results.forEach((result) => all.push(...result.items));
        }
        const seen = new Set();
        this.products = all.filter((product) => {
          if (!product.handle || seen.has(product.handle)) return false;
          seen.add(product.handle);
          return true;
        });
        this.products.forEach((product, index) => { product.sourceOrder = index; });
        CACHE.set(this.cacheKey, this.products);
        this.buildGroups();
      })();
      this.loadPromise = promise;

      try {
        await promise;
      } finally {
        if (this.loadPromise === promise) this.loadPromise = null;
        if (this.loadController === controller) this.loadController = null;
      }
    }

    expandedGroups(token) {
      return this.groups.filter((group) => this.groupMatchesQuery(group, token));
    }

    tokenMatch(product, token) {
      const tokenNormal = normalize(token);
      const tokenCompact = compact(token);
      const groups = this.expandedGroups(token);
      if (groups.length) {
        const constrainedGroups = groups.filter((group) => group.type === 'manufacturer' || group.type === 'model');
        if (constrainedGroups.length && !constrainedGroups.some((group) => this.productMatchesGroup(product, group))) {
          return { matched: false, kind: 'none' };
        }
        const matchingGroups = groups.filter((group) => this.productMatchesGroup(product, group));
        if (matchingGroups.length) {
          const bestType = matchingGroups.some((group) => group.type === 'manufacturer')
            ? 'manufacturer'
            : matchingGroups.some((group) => group.type === 'model') ? 'model' : 'controlled';
          return { matched: true, kind: bestType };
        }
      }
      if (hasDigit(token)) {
        if (product.codeValues.has(codeCompact(token))) return { matched: true, kind: 'exact-code' };
        if (product.searchWords.has(tokenNormal) || product.titleCompact.includes(tokenCompact)) {
          return { matched: true, kind: 'model' };
        }
        if ([...product.codeValues].some((value) => value.includes(codeCompact(token)))) return { matched: true, kind: 'partial-code' };
        return { matched: false, kind: 'none' };
      }
      if (product.searchWords.has(tokenNormal)) return { matched: true, kind: 'exact-word' };
      if (product.searchNormal.includes(tokenNormal) || product.searchCompact.includes(tokenCompact)) return { matched: true, kind: 'contains' };
      if (tokenNormal.length >= 5) {
        const maximum = tokenNormal.length >= 10 ? 2 : 1;
        const fuzzy = [...product.searchWords].some((word) => word.length >= 5 && editDistance(tokenNormal, word, maximum) <= maximum);
        if (fuzzy) return { matched: true, kind: 'fuzzy' };
      }
      return { matched: false, kind: 'none' };
    }

    rank(query) {
      const queryNormal = normalize(query);
      const queryCompact = compact(query);
      let tokens = queryTokens(query).filter((token) => !this.stopwords.has(token));
      if (!tokens.length) tokens = queryTokens(query);

      return this.products.map((product) => {
        const matches = tokens.map((token) => this.tokenMatch(product, token));
        if (matches.some((match) => !match.matched)) return null;

        let score = 0;
        const queryCode = hasDigit(query) ? codeCompact(query) : '';
        const remoteIntent = /\b(?:handsender|funkhandsender|fernbedienung)\b/.test(queryNormal);
        if (remoteIntent) {
          const remoteDevice = /handsender|fernbedienung/.test(product.titleNormal);
          const remoteAccessory = /sonnenblende|halter|antenne|empfaenger|batterie|gehaeuse|abdeckung|clip|tasche|etui/.test(product.titleNormal);
          if (remoteDevice) score += 4200;
          if (remoteAccessory) score -= 5000;
        }
        if (queryCode && product.codeValues.has(queryCode)) score += 20000;
        if (queryCode && (product.seriesCompact === queryCode || product.titleCompact === queryCode)) score += 17000;
        if (queryCode && product.titleCompact.includes(queryCode)) {
          if (/\b(?:torsteuerung|steuerung|steuergeraet|control unit)\b/.test(product.titleNormal)) score += 4200;
          else if (/\b(?:hauptplatine|steuerplatine|ersatzplatine)\b/.test(product.titleNormal)) score += 2200;
          if (/\b(?:optosensor|tastatur|drucktaster|kabel|adapter|halter|halterung|gehaeuse|abdeckung)\b/.test(product.titleNormal)) score -= 5000;
        }
        if (product.titleNormal === queryNormal) score += 15000;
        else if (product.titleNormal.startsWith(queryNormal)) score += 9000;
        else if (product.titleNormal.includes(queryNormal)) score += 6500;
        if (product.manufacturerCompact && product.typeCompact && queryCompact === product.manufacturerCompact + product.typeCompact) score += 13000;
        if (product.typeNormal === queryNormal || product.typeCompact === queryCompact) score += 11000;
        if (product.tagNormal === queryNormal) score += 5500;

        matches.forEach((match) => {
          const points = {
            'exact-code': 6000,
            model: 5000,
            manufacturer: 4500,
            'exact-word': 2800,
            controlled: 2200,
            'partial-code': 1600,
            contains: 1100,
            fuzzy: 450,
          };
          score += points[match.kind] || 0;
        });
        if (product.available) score += 10;
        return { product, score };
      }).filter(Boolean).sort((left, right) => {
        const sortBy = new URLSearchParams(this.sourceParams).get('sort_by') || '';
        const useCollectionOrder = sortBy && sortBy !== 'manual' && sortBy !== 'most-relevant';
        if (useCollectionOrder && left.product.sourceOrder !== right.product.sourceOrder) {
          return left.product.sourceOrder - right.product.sourceOrder;
        }
        return right.score - left.score
          || left.product.title.localeCompare(right.product.title, this.language, { numeric: true })
          || String(left.product.handle).localeCompare(String(right.product.handle));
      }).map((entry) => entry.product);
    }

    suggestions(query) {
      const queryNormal = normalize(query);
      const queryCompact = compact(query);
      const queryWords = words(query);
      const last = queryWords.at(-1) || queryNormal;
      const output = new Map();
      const add = (label, type, count, forcedScore = 0) => {
        const display = capitalise(label);
        const key = compact(display);
        if (!display || !key || key === queryCompact || output.has(key)) return;
        const labelNormal = normalize(display);
        let relevance = 0;
        if (labelNormal.startsWith(queryNormal) || key.startsWith(queryCompact)) relevance = 1100;
        else if (labelNormal.includes(queryNormal) || key.includes(queryCompact)) relevance = 700;
        else if (compact(last).length >= 3 && (key.startsWith(compact(last)) || key.includes(compact(last)))) relevance = 560;
        else if (!hasDigit(query) && queryNormal.length >= 5 && labelNormal.length >= 5) {
          const maximum = Math.max(queryNormal.length, labelNormal.length) >= 10 ? 2 : 1;
          if (editDistance(queryNormal, labelNormal, maximum) <= maximum) relevance = 480;
        }
        if (!relevance && !forcedScore) return;
        const typePriority = type === 'model' ? 500 : type === 'manufacturer' ? 450 : type === 'family' ? 350 : 200;
        output.set(key, { label: display, score: forcedScore + relevance + typePriority + Math.min(count, 50) });
      };

      if (queryWords.length >= 2) {
        const matchedGroups = this.groups.filter((group) => (
          queryWords.some((word) => word.length >= 2 && this.groupMatchesQuery(group, word))
        ));
        const manufacturers = matchedGroups.filter((group) => group.type === 'manufacturer');
        const terms = matchedGroups.filter((group) => (
          group.type === 'family'
          || group.type === 'synonym'
          || group.type === 'intent'
          || group.type === 'model'
          || group.type === 'series'
        ));
        manufacturers.forEach((manufacturer) => {
          terms.forEach((term) => {
            if (compact(manufacturer.primary) === compact(term.primary)) return;
            const count = this.products.reduce((total, product) => total + (
              this.productMatchesGroup(product, manufacturer) && this.productMatchesGroup(product, term) ? 1 : 0
            ), 0);
            if (count) add(`${manufacturer.primary} ${term.primary}`, 'combination', count, 1800);
          });
        });
      }

      const hasManufacturerContext = queryWords.length >= 2 && this.groups.some((group) => (
        group.type === 'manufacturer'
        && queryWords.some((word) => word.length >= 2 && this.groupMatchesQuery(group, word))
      ));
      if (!hasManufacturerContext) {
        this.groups.forEach((group) => {
          if (this.groupMatchesQuery(group, query) || this.groupMatchesQuery(group, last)) add(group.primary, group.type, group.count);
        });
      }
      return [...output.values()]
        .sort((left, right) => right.score - left.score || left.label.localeCompare(right.label, this.language, { numeric: true }))
        .slice(0, 6);
    }

    drawSuggestions(query) {
      const suggestions = this.suggestions(query);
      this.suggestionsList.replaceChildren();
      this.activeSuggestion = -1;
      suggestions.forEach((suggestion, index) => {
        const item = document.createElement('li');
        item.setAttribute('role', 'none');
        const button = document.createElement('button');
        button.type = 'button';
        button.id = `TM24CollectionTerm-${index}`;
        button.className = 'tm24-collection-search__term-suggestion';
        button.dataset.suggestionItem = '';
        button.dataset.suggestionValue = suggestion.label;
        button.setAttribute('role', 'option');
        button.setAttribute('aria-selected', 'false');
        const icon = document.createElement('span');
        icon.className = 'tm24-collection-search__suggestion-icon';
        icon.setAttribute('aria-hidden', 'true');
        icon.textContent = '⌕';
        const label = document.createElement('span');
        appendHighlightedText(label, suggestion.label, query);
        button.append(icon, label);
        item.append(button);
        this.suggestionsList.append(item);
      });
      this.suggestionsWrap.hidden = !suggestions.length;
      if (this.suggestionsEmpty) this.suggestionsEmpty.hidden = Boolean(suggestions.length);
      if (document.activeElement === this.input) this.openSuggestions();
      else this.closeSuggestions();
    }

    async search({ force = false, suggestionsOnly = false, closeSuggestions = false, historyMode = 'replace' } = {}) {
      const query = this.input.value.trim();
      const effectiveQuery = controlledQuery(query, this.language);
      const queryNormal = normalize(effectiveQuery);
      const requestId = ++this.serial;
      this.clearAlternativeLookup({ clearStatus: true });
      if (!query) {
        this.reset({ historyMode });
        return;
      }
      this.updateHistory(query, historyMode);
      if (queryNormal.length < 2) {
        this.status.textContent = this.root.dataset.tooShort || '';
        this.results.hidden = true;
        this.restoreDefaultGrid();
        this.closeSuggestions();
        return;
      }
      if (closeSuggestions) this.closeSuggestions();

      try {
        if (!this.products.length && !suggestionsOnly) this.status.textContent = this.root.dataset.loading;
        await this.load();
        if (requestId !== this.serial || this.destroyed) return;
        if (closeSuggestions) this.closeSuggestions();
        else this.drawSuggestions(query);
        if (suggestionsOnly) {
          this.status.textContent = '';
          if (!this.results.hidden && !this.matches.length) {
            this.scheduleAlternativeCollections(query, effectiveQuery, requestId, false);
          }
          return;
        }
        this.matches = this.rank(effectiveQuery);
        this.renderResults(query);
        if (!this.matches.length) {
          this.scheduleAlternativeCollections(query, effectiveQuery, requestId, force);
        }
      } catch (error) {
        if (requestId !== this.serial || this.destroyed || error.name === 'AbortError') return;
        console.error('[TM24 collection search]', error);
        this.status.textContent = this.root.dataset.error;
        this.results.hidden = true;
        this.closeSuggestions();
        this.restoreDefaultGrid();
      }
    }

    reset({ historyMode = 'replace' } = {}) {
      this.serial += 1;
      clearTimeout(this.timer);
      this.clearAlternativeLookup({ clearStatus: true });
      this.matches = [];
      this.renderedHandles.clear();
      this.status.textContent = '';
      this.cartStatus.textContent = '';
      this.summary.textContent = '';
      this.list.replaceChildren();
      this.results.hidden = true;
      this.more.hidden = true;
      this.more.disabled = false;
      this.more.removeAttribute('aria-busy');
      this.closeSuggestions();
      this.restoreDefaultGrid();
      this.updateHistory('', historyMode);
    }

    money(value) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || this.language, { style: 'currency', currency: this.currency }).format(Number(value || 0) / 100);
      } catch (_) {
        return `${(Number(value || 0) / 100).toFixed(2)} ${this.currency}`;
      }
    }

    createProductCard(product) {
      const card = document.createElement('article');
      card.className = 'tm24-collection-search__card';
      card.dataset.productHandle = product.handle;

      const mediaLink = document.createElement('a');
      mediaLink.href = product.url;
      mediaLink.className = 'tm24-collection-search__media';
      mediaLink.setAttribute('aria-label', product.title);
      if (product.image) {
        const image = document.createElement('img');
        image.src = product.image;
        image.alt = product.title;
        image.loading = 'lazy';
        image.width = 320;
        image.height = 320;
        mediaLink.append(image);
      } else {
        const placeholder = document.createElement('span');
        placeholder.className = 'tm24-collection-search__no-image';
        placeholder.textContent = this.root.dataset.noImage || '';
        mediaLink.append(placeholder);
      }

      const content = document.createElement('div');
      content.className = 'tm24-collection-search__content';
      const titleLink = document.createElement('a');
      titleLink.href = product.url;
      titleLink.className = 'tm24-collection-search__title';
      titleLink.textContent = product.title;
      const price = document.createElement('span');
      price.className = 'tm24-collection-search__price';
      price.textContent = product.priceCents > 0 ? this.money(product.priceCents) : '';
      price.hidden = !(product.priceCents > 0);
      const availability = document.createElement('span');
      availability.className = `tm24-collection-search__availability${product.available ? ' is-available' : ' is-unavailable'}`;
      availability.textContent = product.available ? this.root.dataset.available : this.root.dataset.soldOut;
      content.append(titleLink, price, availability);

      const actions = document.createElement('div');
      actions.className = 'tm24-collection-search__actions';
      if (product.hasOnlyDefaultVariant && product.variantId) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'tm24-collection-search__add';
        button.dataset.cartAdd = '';
        button.dataset.productHandle = product.handle;
        button.textContent = product.available ? this.root.dataset.addToCart : this.root.dataset.soldOut;
        button.disabled = !product.available;
        actions.append(button);
      } else {
        const choose = document.createElement('a');
        choose.href = product.url;
        choose.className = 'tm24-collection-search__choose';
        choose.textContent = this.root.dataset.chooseVariant;
        actions.append(choose);
      }
      content.append(actions);
      card.append(mediaLink, content);
      return card;
    }

    async addToCart(product, button) {
      if (!product.hasOnlyDefaultVariant || !product.variantId || !product.available || button.disabled) return;
      const original = button.textContent;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = this.root.dataset.adding;
      this.cartStatus.textContent = '';

      try {
        const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: String(product.variantId), quantity: 1 }] }),
          signal: this.events.signal,
        });
        let payload = {};
        try {
          payload = await response.json();
        } catch (_) {
          throw new Error('Ungültige Warenkorb-Antwort');
        }
        if (!response.ok || payload.status) throw new Error(payload.description || payload.message || `HTTP ${response.status}`);
        this.cartStatus.textContent = templateText(this.root.dataset.added, product.title, 1);
        const expectedCartCount = headerCartCount() + 1;
        let confirmedCartCount = expectedCartCount;
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
        ensureHeaderCartCount(expectedCartCount);
        fetch(`${root}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: this.events.signal })
          .then((cartResponse) => (cartResponse.ok ? cartResponse.json() : null))
          .then((cart) => {
            if (cart && Number.isFinite(Number(cart.item_count))) confirmedCartCount = Math.max(expectedCartCount, Number(cart.item_count));
            ensureHeaderCartCount(confirmedCartCount);
          })
          .catch(() => {});
        setTimeout(() => ensureHeaderCartCount(confirmedCartCount), 800);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('[TM24 collection cart]', error);
        this.cartStatus.textContent = this.root.dataset.cartError;
      } finally {
        if (button.isConnected) {
          button.disabled = !product.available;
          button.removeAttribute('aria-busy');
          button.textContent = original;
        }
      }
    }

    renderResults(query) {
      this.renderedHandles.clear();
      this.list.replaceChildren();
      const count = this.matches.length;
      const text = count === 1 ? this.root.dataset.resultsOne : this.root.dataset.resultsOther;
      this.status.textContent = '';
      this.summary.textContent = count
        ? templateText(text, query, count)
        : templateText(this.root.dataset.noResults, query, 0);
      this.results.hidden = false;
      this.hideDefaultGrid();
      this.appendBatch(false);
    }

    appendBatch(moveFocus) {
      const start = this.renderedHandles.size;
      const end = Math.min(start + this.pageSize, this.matches.length);
      let firstNewLink = null;
      for (let index = start; index < end; index += 1) {
        const product = this.matches[index];
        if (!product?.handle || this.renderedHandles.has(product.handle)) continue;
        const card = this.createProductCard(product);
        this.renderedHandles.add(product.handle);
        this.list.append(card);
        if (!firstNewLink) firstNewLink = card.querySelector('a');
      }
      this.more.hidden = this.renderedHandles.size >= this.matches.length;
      if (moveFocus && this.more.hidden && firstNewLink) firstNewLink.focus({ preventScroll: true });
    }

    appendMore() {
      if (this.moreBusy || this.more.hidden || this.more.disabled) return;
      this.moreBusy = true;
      this.more.disabled = true;
      this.more.setAttribute('aria-busy', 'true');
      const before = this.renderedHandles.size;
      this.appendBatch(true);
      const added = this.renderedHandles.size - before;
      if (added > 0) this.status.textContent = templateText(this.root.dataset.moreAdded, '', added);
      requestAnimationFrame(() => {
        this.moreBusy = false;
        this.more.disabled = false;
        this.more.removeAttribute('aria-busy');
      });
    }
  }

  CollectionSearch.prototype.stopwords = new Set([
    'ersatzteil', 'ersatzteile', 'original', 'premium', 'tormeister24', 'tortechnik', 'artikelnummer', 'art', 'nr',
    ...(D.stopwords || []),
    ...(D.genericTerms || []),
  ].map(normalize));

  const cleanup = () => {
    INSTANCES.forEach((instance, root) => {
      if (!root.isConnected) instance.destroy();
    });
  };

  const mount = () => {
    cleanup();
    const existingRoot = document.querySelector('[data-tm24-collection-search]');
    if (existingRoot) {
      if (!INSTANCES.has(existingRoot)) INSTANCES.set(existingRoot, new CollectionSearch(existingRoot));
      else INSTANCES.get(existingRoot).syncFilterForms(INSTANCES.get(existingRoot).input.value);
      return;
    }
    const grid = document.getElementById('ProductGridContainer');
    const template = document.getElementById('TM24CollectionSearchTemplate');
    if (!grid || !(template instanceof HTMLTemplateElement)) return;
    const fragment = template.content.cloneNode(true);
    const root = fragment.querySelector('[data-tm24-collection-search]');
    if (!root) return;
    grid.insertBefore(fragment, grid.firstChild);
    INSTANCES.set(root, new CollectionSearch(root));
  };

  const api = { mount, instances: INSTANCES };
  window[NAMESPACE] = api;

  mount();
  document.addEventListener('DOMContentLoaded', mount, { once: true, signal: GLOBAL_EVENTS.signal });
  document.addEventListener('shopify:section:load', mount, { signal: GLOBAL_EVENTS.signal });
  document.addEventListener('shopify:section:unload', cleanup, { signal: GLOBAL_EVENTS.signal });
  window.addEventListener('pageshow', mount, { signal: GLOBAL_EVENTS.signal });
  window.addEventListener('popstate', () => {
    mount();
    INSTANCES.forEach((instance) => instance.applyUrlState());
  }, { signal: GLOBAL_EVENTS.signal });

  const observerRoot = document.getElementById('PageContainer') || document.body;
  if (observerRoot) {
    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        mount();
      });
    });
    observer.observe(observerRoot, { childList: true, subtree: true });
    api.observer = observer;
  }
})();
